import React, { useState } from 'react'
import { ActivityIndicator, Linking, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Icon, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import {
  type AdminOrganizerRequest,
  useAdminOrganizerRequests,
  useApproveOrganizerRequest,
  useRejectOrganizerRequest,
} from '@hooks'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { AdminPage } from './components/AdminPage'
import { PersonAvatar } from './components/PersonAvatar'
import { ReasonModal } from './components/ReasonModal'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminRequests'>

/** Pending "become an organizer" requests, to validate or refuse. */
export const AdminRequests: React.FC<Props> = ({ navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const { data, isLoading } = useAdminOrganizerRequests()
  const approve = useApproveOrganizerRequest()
  const reject = useRejectOrganizerRequest()
  const [rejecting, setRejecting] = useState<AdminOrganizerRequest | null>(null)
  const requests = data?.data ?? []

  return (
    <AdminPage
      title={t('admin.requests.title')}
      onBack={() => navigation.navigate('AdminHome')}
      active="requests"
    >
      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : requests.length === 0 ? (
        <View style={styles.empty} testID="admin-requests-empty">
          <Icon name="check" size={40} color={theme.colors.green} />
          <Text type="subtitleBold" center>
            {t('admin.requests.empty')}
          </Text>
          <Text type="body" center color={theme.colors.greyBlue}>
            {t('admin.requests.emptyHint')}
          </Text>
        </View>
      ) : (
        requests.map((request) => (
          <RequestCard
            key={request.id}
            request={request}
            busy={approve.isPending || reject.isPending}
            onApprove={() => approve.mutate(request.id)}
            onReject={() => setRejecting(request)}
          />
        ))
      )}
      <ReasonModal
        isVisible={rejecting !== null}
        title={t('admin.requests.rejectTitle')}
        description={t('admin.requests.rejectDescription', {
          name: rejecting ? `${rejecting.account.firstName} ${rejecting.account.lastName}` : '',
        })}
        label={t('admin.requests.rejectReasonLabel')}
        confirmLabel={t('admin.requests.reject')}
        isLoading={reject.isPending}
        onClose={() => setRejecting(null)}
        onConfirm={(reason) => {
          if (!rejecting) return
          reject.mutate({ id: rejecting.id, reason }, { onSuccess: () => setRejecting(null) })
        }}
      />
    </AdminPage>
  )
}

function RequestCard({
  request,
  busy,
  onApprove,
  onReject,
}: {
  request: AdminOrganizerRequest
  busy: boolean
  onApprove: () => void
  onReject: () => void
}) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const { account } = request
  const locale = currentLanguage === 'en' ? 'en-GB' : currentLanguage === 'es' ? 'es-ES' : 'fr-FR'
  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(
    new Date(request.createdAt),
  )

  return (
    <View style={styles.card} testID={`admin-request-${request.id}`}>
      <View style={styles.person}>
        <PersonAvatar
          uri={account.avatarUrl}
          firstName={account.firstName}
          lastName={account.lastName}
          size={52}
        />
        <View style={styles.personBody}>
          <Text type="subtitleBold" numberOfLines={1}>
            {`${account.firstName} ${account.lastName}`.trim()}
          </Text>
          <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
            {account.email}
          </Text>
          {account.jobTitle ? (
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue} numberOfLines={1}>
              {account.jobTitle}
            </Text>
          ) : null}
        </View>
      </View>
      <View style={styles.details}>
        <Text type="bodySmallBold" color={theme.colors.secondary}>
          {t('admin.requests.organization')}
        </Text>
        <Text type="body">{request.organizationName}</Text>
        <Text type="bodySmallBold" color={theme.colors.secondary}>
          {t('admin.requests.message')}
        </Text>
        <Text type="body">{request.message}</Text>
        {request.websiteUrl ? (
          <TouchableOpacity
            accessibilityRole="link"
            onPress={() => Linking.openURL(request.websiteUrl as string)}
          >
            <Text type="bodySmall" color={theme.colors.primary} numberOfLines={1}>
              {request.websiteUrl}
            </Text>
          </TouchableOpacity>
        ) : null}
        <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
          {t('admin.requests.sentOn', { date })}
        </Text>
      </View>
      <View style={styles.actions}>
        <Button
          title={t('admin.requests.reject')}
          isRed
          flex
          disabled={busy}
          onPress={onReject}
        />
        <Button title={t('admin.requests.approve')} isGreen flex disabled={busy} onPress={onApprove} />
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    empty: { alignItems: 'center', gap: theme.spacing.sm, paddingVertical: theme.spacing.xxxl * 2 },
    card: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    person: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
    personBody: { flex: 1, minWidth: 0, gap: 2 },
    details: { gap: theme.spacing.xs },
    actions: { flexDirection: 'row', gap: theme.spacing.md },
  })
